import { PtzParameters } from "../stores/types";
import { useEffect, useRef, MutableRefObject, RefObject, useMemo } from "react";
import * as THREE from "three";
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import modelPath from "../../assets/models/fisheye_hemi.obj";
 
interface IArgs {
    videoRef: MutableRefObject<HTMLVideoElement | null>;
    canUsePTZ?: boolean;
    containerRef?: RefObject<HTMLDivElement>;
    ptzCanvasRef?: MutableRefObject<HTMLCanvasElement | null>;
    ptzParametersRef?: MutableRefObject<PtzParameters | null>;
    isLoading?: boolean;
    setIsLoading?: (value: boolean) => void
}
 
const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
 
const fragmentShader = `
  uniform sampler2D tDiffuse;
  uniform float distortion;
  varying vec2 vUv;    
 
  void main() {
    gl_FragColor = texture2D(tDiffuse, vUv);
  }
`;
 
const CAMERA_DEFAULT_FOV = 70; // Start with a wider view
const MIN_FOV = 30;
const MAX_FOV = 90; // Allow zooming out enough to see the whole circle
const MAX_TILT_UP = -0.15;
const MAX_TILT_DOWN = -Math.PI;
 
export const useFishEyeToSingleView = ({
    videoRef,
    canUsePTZ,
    containerRef,
    ptzCanvasRef,
    ptzParametersRef,
    isLoading,
    setIsLoading,
}: IArgs) => {
    const mouseDownRef = useRef(false);
 
    // --- Mobile Refs ---
    const previousTouchRef = useRef<{ x: number; y: number } | null>(null);
    const pinchDistanceRef = useRef<number | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const meshRef = useRef<THREE.Mesh | null>(null);
 
 
    useEffect(() => {
        if (!videoRef.current || !containerRef?.current) return;
 
        if (!canUsePTZ) {
            videoRef.current.style.width = "auto";
            containerRef.current.style.width = "100%";
            if (containerRef.current.parentElement) {
                containerRef.current.parentElement.style.width = "100%";
            }
            return;
        }
 
        setIsLoading?.(true);
 
        videoRef.current.style.display = "none";
 
        const renderer = new THREE.WebGLRenderer({ preserveDrawingBuffer: true, antialias: true });
        renderer.setPixelRatio(window.devicePixelRatio || 1);
 
        containerRef.current.appendChild(renderer.domElement);
        if (ptzCanvasRef) ptzCanvasRef.current = renderer.domElement;
 
        const scene = new THREE.Scene();
        const videoTexture = new THREE.VideoTexture(videoRef.current);
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.magFilter = THREE.LinearFilter;
 
        const camera = new THREE.PerspectiveCamera(
            ptzParametersRef?.current?.fov ?? CAMERA_DEFAULT_FOV,
            1,
            0.1,
            1000
        );
        cameraRef.current = camera;
 
        // camera.position.set(0, 0, 0);
        const CAMERA_DISTANCE = 0.01; // critical
        camera.position.set(0, 0, CAMERA_DISTANCE);
 
        camera.lookAt(new THREE.Vector3(0, -1, 1));
 
        const setRendererSize = () => {
            if (!containerRef.current) return;
 
            let { width, height } = containerRef.current.getBoundingClientRect();
 
            if (height === 0) {
                const parent = containerRef.current.parentElement;
                if (parent) {
                    const rect = parent.getBoundingClientRect();
                    width = rect.width;
                    height = rect.height || rect.width * 9 / 16;
                } else {
                    width = 800;
                    height = 450;
                }
            }
 
            renderer.setSize(width, height, false);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
        };
 
        requestAnimationFrame(() => {
            setRendererSize();
        });
 
        const resizeObserver = new ResizeObserver(() => setRendererSize());
        resizeObserver.observe(containerRef.current);
 
        //let mesh: THREE.Mesh | undefined;
        const loader = new OBJLoader();
        loader.load(
            modelPath,
            (obj) => {
                obj.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        child.material = new THREE.ShaderMaterial({
                            uniforms: { tDiffuse: { value: videoTexture } },
                            vertexShader,
                            fragmentShader,
                            side: THREE.DoubleSide
                        });
                        scene.add(child);
                        // mesh = child;
                        meshRef.current = child;
 
                        if (ptzParametersRef?.current) {
                            meshRef.current!.rotation.y = ptzParametersRef.current.pan;
                        }
                    }
                });
                // if (videoRef.current) {
                //     videoRef.current.style.display = "none";
                // }
                setIsLoading?.(false);
            },
            undefined,
            (err) => {
                console.error("Error loading OBJ", err);
                setIsLoading?.(false);
            }
        );
 
        if (ptzParametersRef?.current) {
            camera.rotation.x = ptzParametersRef.current.tilt;
            camera.fov = ptzParametersRef.current.fov;
            camera.updateProjectionMatrix();
        }
 
        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            const delta = e.deltaX || -e.deltaY;
 
            camera.fov = Math.max(
                MIN_FOV,
                Math.min(MAX_FOV, camera.fov - delta / 100)
            );
 
            camera.updateProjectionMatrix();
 
            if (ptzParametersRef?.current) {
                ptzParametersRef.current.fov = camera.fov;
            }
        };
 
        const handleMouseDown = (e: MouseEvent) => {
            if (e.button === 0) mouseDownRef.current = true;
        };
 
        const handleMouseMove = (e: MouseEvent) => {
            if (!mouseDownRef.current || !canUsePTZ) return;
 
            const { movementX, movementY } = e;
 
            // PAN — full 360°
            if (movementX && meshRef.current) {
                meshRef.current.rotation.y -= movementX / 300;
            }
 
            // TILT — full hemisphere (FIXED LIMITS)
            if (movementY) {
                const newTilt = camera.rotation.x - movementY / 300;
                camera.rotation.x = Math.max(
                    MAX_TILT_DOWN,
                    Math.min(MAX_TILT_UP, newTilt)
                );
            }
 
            camera.updateProjectionMatrix();
 
            if (ptzParametersRef?.current) {
                ptzParametersRef.current.pan = meshRef.current?.rotation.y ?? 0;
                ptzParametersRef.current.tilt = camera.rotation.x;
                ptzParametersRef.current.fov = camera.fov;
            }
        };
 
        const handleMouseUp = () => {
            mouseDownRef.current = false;
        };
 
        // Mobile Events (Touch)
        // -----------------------
        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 1) {
                mouseDownRef.current = true;
                previousTouchRef.current = {
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY
                };
            } else if (e.touches.length === 2) {
                mouseDownRef.current = false;
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                pinchDistanceRef.current = Math.hypot(dx, dy);
            }
        };
 
        const handleTouchMove = (e: TouchEvent) => {
            if (e.cancelable) e.preventDefault();
            if (!canUsePTZ) return;
 
            if (e.touches.length === 1 && mouseDownRef.current && previousTouchRef.current) {
                const touch = e.touches[0];
                const deltaX = touch.clientX - previousTouchRef.current.x;
                const deltaY = touch.clientY - previousTouchRef.current.y;
 
                if (meshRef.current) {
                    meshRef.current.rotation.y -= deltaX / 100;
                }
 
                //cameraRef.current.rotation.x -= deltaY / 100;
                const newTilt = cameraRef.current.rotation.x - deltaY / 300;
                cameraRef.current.rotation.x = Math.max(
                    MAX_TILT_DOWN,
                    Math.min(MAX_TILT_UP, newTilt)
                );
 
                previousTouchRef.current = {
                    x: touch.clientX,
                    y: touch.clientY
                };
            }
 
            else if (e.touches.length === 2) {
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                const currentDist = Math.hypot(dx, dy);
 
                // If this is the start of the pinch gesture, initialize distance
                if (pinchDistanceRef.current === null) {
                    pinchDistanceRef.current = currentDist;
                    return;
                }
 
                const distDelta = currentDist - pinchDistanceRef.current;
 
                // Adjust camera FOV based on pinch distance
                cameraRef.current.fov -= distDelta * 0.25;   // Sensitivity of zoom
 
                pinchDistanceRef.current = currentDist; // Store new pinch distance
            }
        };
 
        const handleTouchEnd = () => {
            mouseDownRef.current = false;
            previousTouchRef.current = null;
            pinchDistanceRef.current = null;
        };
 
        const element = containerRef.current;
        element.addEventListener("wheel", handleWheel, { passive: false });
        element.addEventListener("mousedown", handleMouseDown);
        element.addEventListener("mousemove", handleMouseMove);
 
        element.addEventListener("touchstart", handleTouchStart, { passive: false });
        element.addEventListener("touchmove", handleTouchMove, { passive: false });
        element.addEventListener("touchend", handleTouchEnd);
        element.addEventListener("touchcancel", handleTouchEnd);
 
        document.body.addEventListener("mouseup", handleMouseUp);
        document.body.addEventListener("mouseleave", handleMouseUp);
 
        let animationId: number;
        const animate = () => {
            animationId = requestAnimationFrame(animate);
            renderer.render(scene, cameraRef.current);
        };
        animate();
 
        return () => {
            setIsLoading?.(false);
            if (animationId) cancelAnimationFrame(animationId);
 
            resizeObserver.disconnect();
 
            element.removeEventListener("wheel", handleWheel);
            element.removeEventListener("mousedown", handleMouseDown);
            element.removeEventListener("mousemove", handleMouseMove);
 
            element.removeEventListener("touchstart", handleTouchStart);
            element.removeEventListener("touchmove", handleTouchMove);
            element.removeEventListener("touchend", handleTouchEnd);
            element.removeEventListener("touchcancel", handleTouchEnd);
 
            document.body.removeEventListener("mouseup", handleMouseUp);
            document.body.removeEventListener("mouseleave", handleMouseUp);
 
            renderer.dispose();
            if (renderer.domElement.parentElement === containerRef.current) {
                containerRef.current?.removeChild(renderer.domElement);
            }
            if (ptzCanvasRef?.current) ptzCanvasRef.current = null;
 
            if (videoRef.current) {
                videoRef.current.style.display = "block";
                //videoRef.current.style.borderRadius = "5px";
            }
        };
    }, [videoRef.current, containerRef, canUsePTZ, ptzCanvasRef, ptzParametersRef]);
};